import { useState } from 'react'
import api from '../../../services/api'
import Button from '../../../components/common/Button'

interface GalleryCategory {
  id: number
  name: string
  slug: string
  position: number
  media_count: number
}

interface CategoryManagerProps {
  categories: GalleryCategory[]
  onCategoriesChanged: () => void
}

function CategoryManager({ categories, onCategoriesChanged }: CategoryManagerProps) {
  const [newCategoryName, setNewCategoryName] = useState('')
  const [editingId, setEditingId] = useState<number | null>(null)
  const [editingName, setEditingName] = useState('')
  const [submitting, setSubmitting] = useState(false)

  const handleAddCategory = async () => {
    if (!newCategoryName.trim()) return

    setSubmitting(true)
    try {
      await api.post('/gallery/categories', {
        name: newCategoryName.trim(),
        position: categories.length,
      })
      setNewCategoryName('')
      onCategoriesChanged()
    } catch (err) {
      alert(err instanceof Error ? err.message : 'Erreur lors de la création')
    } finally {
      setSubmitting(false)
    }
  }

  const handleStartEdit = (category: GalleryCategory) => {
    setEditingId(category.id)
    setEditingName(category.name)
  }

  const handleSaveEdit = async () => {
    if (!editingId || !editingName.trim()) return

    setSubmitting(true)
    try {
      await api.put(`/gallery/categories/${editingId}`, {
        name: editingName.trim(),
      })
      setEditingId(null)
      setEditingName('')
      onCategoriesChanged()
    } catch (err) {
      alert(err instanceof Error ? err.message : 'Erreur lors de la mise à jour')
    } finally {
      setSubmitting(false)
    }
  }

  const handleCancelEdit = () => {
    setEditingId(null)
    setEditingName('')
  }

  const handleDelete = async (category: GalleryCategory) => {
    if (category.media_count > 0) {
      alert(`Cette catégorie contient ${category.media_count} média(s). Veuillez d'abord les déplacer ou les supprimer.`)
      return
    }

    if (!confirm(`Supprimer la catégorie "${category.name}" ?`)) return

    setSubmitting(true)
    try {
      await api.delete(`/gallery/categories/${category.id}`)
      onCategoriesChanged()
    } catch (err) {
      alert(err instanceof Error ? err.message : 'Erreur lors de la suppression')
    } finally {
      setSubmitting(false)
    }
  }

  return (
    <div className="category-manager">
      {/* Category List */}
      <div className="category-list">
        {categories.length === 0 ? (
          <p style={{ color: '#64748b', textAlign: 'center', padding: '1rem' }}>
            Aucune catégorie créée
          </p>
        ) : (
          categories.map((category) => (
            <div key={category.id} className="category-item">
              {editingId === category.id ? (
                <>
                  <input
                    type="text"
                    value={editingName}
                    onChange={(e) => setEditingName(e.target.value)}
                    onKeyDown={(e) => {
                      if (e.key === 'Enter') handleSaveEdit()
                      if (e.key === 'Escape') handleCancelEdit()
                    }}
                    autoFocus
                    style={{
                      flex: 1,
                      padding: '0.375rem 0.5rem',
                      border: '1px solid #2563eb',
                      borderRadius: '0.25rem',
                      marginRight: '0.5rem',
                    }}
                  />
                  <div className="category-item-actions">
                    <button
                      className="edit-btn"
                      onClick={handleSaveEdit}
                      disabled={submitting}
                    >
                      ✓
                    </button>
                    <button
                      className="delete-btn"
                      onClick={handleCancelEdit}
                      disabled={submitting}
                    >
                      ✕
                    </button>
                  </div>
                </>
              ) : (
                <>
                  <div className="category-item-info">
                    <span className="category-item-name">{category.name}</span>
                    <span className="category-item-count">
                      {category.media_count} média{category.media_count !== 1 ? 's' : ''}
                    </span>
                  </div>
                  <div className="category-item-actions">
                    <button
                      className="edit-btn"
                      onClick={() => handleStartEdit(category)}
                      disabled={submitting}
                    >
                      Modifier
                    </button>
                    <button
                      className="delete-btn"
                      onClick={() => handleDelete(category)}
                      disabled={submitting}
                    >
                      Supprimer
                    </button>
                  </div>
                </>
              )}
            </div>
          ))
        )}
      </div>

      {/* Add New Category */}
      <div className="category-form">
        <input
          type="text"
          value={newCategoryName}
          onChange={(e) => setNewCategoryName(e.target.value)}
          placeholder="Nom de la nouvelle catégorie"
          onKeyDown={(e) => {
            if (e.key === 'Enter') handleAddCategory()
          }}
        />
        <Button
          onClick={handleAddCategory}
          disabled={!newCategoryName.trim() || submitting}
        >
          Ajouter
        </Button>
      </div>
    </div>
  )
}

export default CategoryManager
